import { useCallback } from 'react';
import { ApolloClient } from '@apollo/client';
import { SaveDocument, SaveDocumentTemplate } from '../../../../../utils/api/document-interface.types';
import { useDocumentState } from '../../../../../contexts/DocumentContext';
import { documentStateFromResponse } from '../../../../../utils/document-wizard-utils/document-state-from-response';

type UseLeaseDocumentSaveOptions = {
  id: string | number;
  onSuccess?: () => void;
  onError: () => void;
  save: SaveDocument;
  apolloClient: ApolloClient<object>;
};

export const useDocumentSave = ({
  id,
  save,
  onSuccess,
  onError,
  apolloClient,
}: UseLeaseDocumentSaveOptions): ((document: { body: string; documentVersion?: number; isAutosave?: boolean }) => void) => {
  const { state, stateRef, updateDocumentState } = useDocumentState();
  const storedDocument = state.document;

  const toBase64 = (str: string | number | undefined): string => {
    return str ? btoa(unescape(encodeURIComponent(String(str)))) : '';
  };

  return useCallback(
    (document) => {
      const documentWithHeaderAndFooterBase64: any = {};

      if (document?.body !== undefined) {
        documentWithHeaderAndFooterBase64.body = toBase64(document.body || '');
      }

      if (storedDocument?.header) {
        documentWithHeaderAndFooterBase64.header = toBase64(storedDocument.header);
      }

      if (storedDocument?.footer) {
        documentWithHeaderAndFooterBase64.footer = toBase64(storedDocument.footer);
      }

      const isAutosave = document?.isAutosave;
      const title = stateRef.current.title;
      const documentId = stateRef.current.id;

      if (!isAutosave) {
        updateDocumentState({ isPending: true });
      }

      const fieldsForUpdate = {
        documentId: String(id),
        document: documentWithHeaderAndFooterBase64,
        title: documentId ? title : null,
      };

      const savePromise = save(fieldsForUpdate)(apolloClient)
        .then((response: any) => {
          if (!isAutosave) {
            updateDocumentState(documentStateFromResponse(response, false));
          }

          // Skip Apollo cache update during autosave
          if (!isAutosave && (response.updatedAt || response.document)) {
            try {
              apolloClient.cache.modify({
                id: apolloClient.cache.identify({ __typename: 'Document', id }),
                fields: {
                  updatedAt() {
                    return response.updatedAt;
                  },
                  lastUpdated() {
                    return response.updatedAt;
                  },
                  document(existingDocument: any) {
                    return response.document || existingDocument;
                  },
                },
              });
            } catch (error) {
              // Silently fail cache update - not critical
            }
          }

          if (typeof onSuccess === 'function') {
            onSuccess();
          }
        })
        .catch((error: any) => {
          const isPostApprovalError =
            (window as any).JUST_APPROVED_DOCUMENT &&
            (error.message?.includes('cannot be edited') || error.message?.includes('The document cannot be edited'));

          if (isPostApprovalError) {
            return;
          }

          if (typeof onError === 'function') {
            onError();
          }
        })
        .finally(() => {
          if (!isAutosave) {
            updateDocumentState({ isPending: false });
          }
        });

      // Skip optimistic write during autosave
      if (document && !document?.isAutosave) {
        const documentWithHeaderAndFooter = {
          body: document.body,
          header: storedDocument?.header || '',
          footer: storedDocument?.footer || '',
        };
        updateDocumentState({ document: documentWithHeaderAndFooter });
      }

      return savePromise;
    },
    [apolloClient, id, onError, onSuccess, save, storedDocument, stateRef, updateDocumentState]
  );
};

type UseLeaseDocumentTemplateSaveOptions = {
  id: string | number;
  onSuccess?: () => void;
  onError: () => void;
  save: SaveDocumentTemplate;
  apolloClient: ApolloClient<object>;
};

export const useLeaseDocumentTemplateSave = ({
  id,
  save,
  onSuccess,
  onError,
  apolloClient,
}: UseLeaseDocumentTemplateSaveOptions): ((document: { body: string; documentVersion?: number; isAutosave?: boolean }) => void) => {
  const { state, stateRef, updateDocumentState } = useDocumentState();
  const storedDocument = state.template;

  const toBase64 = (str: string | number | undefined): string => {
    return str ? btoa(unescape(encodeURIComponent(String(str)))) : '';
  };

  return useCallback(
    (document) => {
      if (!id || id === '') {
        return Promise.resolve();
      }

      const documentTemplateWithHeaderAndFooterBase64: any = {};

      if (document?.body !== undefined) {
        documentTemplateWithHeaderAndFooterBase64.body = toBase64(document.body || '');
      }

      if (storedDocument?.header) {
        documentTemplateWithHeaderAndFooterBase64.header = toBase64(storedDocument.header);
      }

      if (storedDocument?.footer) {
        documentTemplateWithHeaderAndFooterBase64.footer = toBase64(storedDocument.footer);
      }

      const isAutosave = document?.isAutosave;

      if (!isAutosave) {
        updateDocumentState({ isPending: true });
      }

      const fieldsForUpdate: any = {
        documentTemplateId: id,
      };

      if (documentTemplateWithHeaderAndFooterBase64) {
        fieldsForUpdate['template'] = documentTemplateWithHeaderAndFooterBase64;
      }

      const savePromise = save(fieldsForUpdate)(apolloClient)
        .then((response: any) => {
          if (!isAutosave) {
            updateDocumentState(documentStateFromResponse(response, false));
          }

          // Skip Apollo cache update during autosave
          if (!isAutosave && response.updatedAt) {
            try {
              apolloClient.cache.modify({
                id: apolloClient.cache.identify({ __typename: 'DocumentTemplate', id }),
                fields: {
                  updatedAt() {
                    return response.updatedAt;
                  },
                  draftUpdatedAt() {
                    return response.updatedAt;
                  },
                },
              });
            } catch (error) {
              // Silently fail cache update - not critical
            }
          }

          // Increment placeholder refresh key — intentionally kept during autosave
          const currentRefreshKey = stateRef.current.placeholderRefreshKey;
          updateDocumentState({ placeholderRefreshKey: (currentRefreshKey || 0) + 1 });

          if (typeof onSuccess === 'function') {
            onSuccess();
          }
        })
        .catch((error: any) => {
          const isPostApprovalError =
            (window as any).JUST_APPROVED_DOCUMENT &&
            (error.message?.includes('cannot be edited') || error.message?.includes('The document cannot be edited'));

          if (isPostApprovalError) {
            return;
          }

          if (typeof onError === 'function') {
            onError();
          }
        })
        .finally(() => {
          if (!isAutosave) {
            updateDocumentState({ isPending: false });
          }
        });

      // Skip optimistic write during autosave
      if (document && !document?.isAutosave) {
        const documentTemplateWithHeaderAndFooter = {
          body: document.body,
          header: storedDocument?.header || '',
          footer: storedDocument?.footer || '',
        };
        updateDocumentState({ template: documentTemplateWithHeaderAndFooter });
      }

      return savePromise;
    },
    [apolloClient, id, onError, onSuccess, save, storedDocument, stateRef, updateDocumentState]
  );
};
